import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Calendar, Users } from 'lucide-react';
import { useSteamUser } from '../hooks/useSteamUser';
import { StatsOverview } from '../components/StatsOverview';
import { ActivityFeed } from '../components/ActivityFeed';
import { UserProfileHeader } from '../components/UserProfileHeader';
import { TopPlayedGames } from '../components/TopPlayedGames';
import { FriendsList } from '../components/FriendsList';
import { BadgeCollection } from '../components/BadgeCollection';
import { calculateBadges } from '../utils/badges';

export function UserProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, gameStats, isLoading, error } = useSteamUser(username || '');

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user || !gameStats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'User not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const totalPlaytimeHours = Math.floor(gameStats.totalPlaytime / 60);
  const totalAchievements = gameStats.achievementStats.unlocked;
  const earnedBadges = calculateBadges(totalPlaytimeHours, totalAchievements);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <UserProfileHeader user={user} gameStats={gameStats} />

        {/* Stats Overview */}
        <div className="mt-6">
          <StatsOverview stats={gameStats} username={username || ''} />
        </div>

        {/* Main Content */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info & Badges */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
              <div className="space-y-4">
                {user.timecreated && (
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-5 h-5 mr-3" />
                    <span>Member since {formatDate(user.timecreated)}</span>
                  </div>
                )}
                {user.loccountrycode && (
                  <div className="flex items-center text-gray-600">
                    <Globe className="w-5 h-5 mr-3" />
                    <span>Location: {user.loccountrycode}</span>
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Users className="w-5 h-5 mr-3" />
                  <a
                    href={user.profileurl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700"
                  >
                    View Steam Profile
                  </a>
                </div>
              </div>
            </div>

            <BadgeCollection 
              earnedBadges={earnedBadges}
              totalPlaytime={totalPlaytimeHours}
              username={username || ''}
            />

            <FriendsList steamId={user.steamid} friendCount={user.friendCount || 0} />

            {gameStats.mostPlayedGames && gameStats.mostPlayedGames.length > 0 && (
              <TopPlayedGames games={gameStats.mostPlayedGames.slice(0, 10)} />
            )}
          </div>

          {/* Right Column - Activity Feed */}
          <div className="lg:col-span-2">
            {gameStats && (
              <ActivityFeed
                achievements={gameStats.achievements.flatMap(game => 
                  game.achievements.map(a => ({ ...a, gameName: game.name, appid: game.appid }))
                )}
                recentGames={gameStats.recentGames}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}